import { prospectScopeSchema } from "@amtana/prospect-contract";
import { z } from "zod";

// An export is a file (or a push to Smartlead) of the prospects in a scope:
// an analysis, a filter, or the global prospect table. Its download page
// doubles as a shareable link.

export const exportFormatSchema = z.enum(["csv", "excel", "smartlead"]);
export type ExportFormat = z.infer<typeof exportFormatSchema>;

export const exportStatusSchema = z.enum(["pending", "processing", "completed", "failed"]);
export type ExportStatus = z.infer<typeof exportStatusSchema>;

export const exportSourceSchema = z.object({
  kind: z.enum(["analysis", "list", "prospects"]),
  id: z.string().nullable(),
  name: z.string(),
});
export type ExportSource = z.infer<typeof exportSourceSchema>;

/** Prospect fields a file can carry, beyond name, username, email and phone. */
export const exportColumnSchema = z.enum([
  "firstName",
  "lastName",
  "website",
  "followers",
  "following",
  "biography",
  "platform",
  "qualification",
  "country",
  "language",
  "niche",
  "category",
  "address",
  "rating",
  "reviews",
]);
export type ExportColumn = z.infer<typeof exportColumnSchema>;

export const exportSchema = z.object({
  id: z.string(),
  source: exportSourceSchema,
  format: exportFormatSchema,
  columns: z.array(exportColumnSchema),
  status: exportStatusSchema,
  totalRows: z.number().int().nonnegative(),
  createdAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
});
export type Export = z.infer<typeof exportSchema>;

export const createExportInputSchema = z.object({
  source: exportSourceSchema,
  scope: prospectScopeSchema,
  format: exportFormatSchema,
  columns: z.array(exportColumnSchema).max(20),
});
export type CreateExportInput = z.infer<typeof createExportInputSchema>;

export const exportListSchema = z.object({ exports: z.array(exportSchema) });
export type ExportList = z.infer<typeof exportListSchema>;
export const exportIdInputSchema = z.object({ exportId: z.string() });
export const emptyInputSchema = z.object({});

export const exportFileSchema = z.object({
  filename: z.string(),
  mimeType: z.string(),
  body: z.string(),
});
export type ExportFile = z.infer<typeof exportFileSchema>;
